/*
 * PAWS & PEARLS — Candy Dreams | Bright Pink Petal Flight V1
 * 25 October 2026
 *
 * Independent hero overlay using seven isolated bright-pink petals.
 * Intended as a new addition to the Candy Dreams hero only.
 * It does NOT modify existing rose, blossom, butterfly, curtain, sound,
 * falling-petal or vine artwork/controllers.
 *
 * Trigger logic:
 * - Observe the REAL first hanging rose's frame progression.
 * - When the rose reaches its near-finish state, wait a short theatrical delay.
 * - Then release the bright-pink petals from the left side of the hero.
 *
 * Behaviour:
 * - One decorative pass per page load.
 * - Petals drift individually from left to right at varied heights.
 * - They rise/dip gently and rotate for a soft floating effect.
 *
 * Liquid must provide seven asset URLs in data-pp-bright-petal-urls.
 */
(function () {
  'use strict';

  var script = document.currentScript;
  if (!script || !script.dataset.ppBrightPetalUrls) {
    console.warn('[P&P Bright Pink Petals] Missing Liquid-resolved petal URLs.');
    return;
  }

  var sources = script.dataset.ppBrightPetalUrls.split('|').map(function (url) {
    return url.trim();
  }).filter(Boolean);
  if (sources.length !== 7) {
    console.warn('[P&P Bright Pink Petals] Exactly seven transparent petal images are required.');
    return;
  }

  if (!/(?:^|\/)collections\/candy-dreams\/?$/i.test(location.pathname)) return;

  var DIVIDER = '.pp-candy-floral-divider';
  var ROSE = 'pp-candy-first-hanging-rose-v1';
  var ROOT = 'pp-candy-bright-petal-flight-v1';
  var START_DELAY_MS = 3200;
  var RUN_MS = 9800;
  var Z_INDEX = 8;
  var mounted = new WeakSet();
  var reducedMotion = Boolean(window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  var imageReady = Promise.all(sources.map(function (src) {
    return new Promise(function (resolve, reject) {
      var image = new Image();
      image.onload = function () { resolve(image); };
      image.onerror = function () { reject(new Error(src)); };
      image.src = src;
      if (image.complete && image.naturalWidth > 0) resolve(image);
    });
  })).then(function (images) {
    return images;
  }).catch(function (error) {
    console.warn('[P&P Bright Pink Petals] A petal image did not load. No flight shown.', error);
    return null;
  });

  function roseAlmostFinished(rose) {
    var pos = rose.style.backgroundPosition || '';
    var parsed = pos.match(/^\s*([\d.]+)%\s+([\d.]+)%/);
    return Boolean(parsed && Number(parsed[2]) >= 99 && Number(parsed[1]) >= 65);
  }

  function findHero() {
    var divider = document.querySelector(DIVIDER);
    if (!divider) return null;
    var section = divider.closest('[id^="shopify-section-"]') ||
      divider.closest('.shopify-section');
    if (!section) return null;
    var hero = section.nextElementSibling;
    if (!hero || !hero.matches('[id^="shopify-section-"],.shopify-section') ||
        !hero.querySelector('img,picture,video')) return null;
    return hero;
  }

  function ease(t) {
    t = Math.max(0, Math.min(1, t));
    return t * t * (3 - 2 * t);
  }

  function buildSpecs(hero) {
    var w = hero.clientWidth || 1440;
    var h = hero.clientHeight || 560;
    return [
      {src:0, start:0.00, dur:0.71, size:0.98, y0:0.62, y1:0.36, amp:0.038, rot:-22, rot2:15},
      {src:1, start:0.09, dur:0.69, size:1.06, y0:0.51, y1:0.24, amp:0.032, rot:14, rot2:-18},
      {src:2, start:0.17, dur:0.66, size:0.94, y0:0.72, y1:0.47, amp:0.041, rot:-16, rot2:12},
      {src:3, start:0.30, dur:0.62, size:0.88, y0:0.56, y1:0.31, amp:0.029, rot:18, rot2:-14},
      {src:4, start:0.41, dur:0.54, size:0.84, y0:0.68, y1:0.43, amp:0.036, rot:-14, rot2:10},
      {src:5, start:0.52, dur:0.48, size:0.74, y0:0.60, y1:0.34, amp:0.026, rot:21, rot2:-11},
      {src:6, start:0.63, dur:0.41, size:0.62, y0:0.49, y1:0.28, amp:0.022, rot:-10, rot2:8}
    ].map(function (spec, i) {
      var baseW = Math.min(Math.max(w * 0.07 * spec.size, 34), 84);
      return {
        imgIndex: spec.src,
        startMs: spec.start * RUN_MS,
        endMs: Math.min(RUN_MS, (spec.start + spec.dur) * RUN_MS),
        width: baseW,
        fromX: -baseW - 20 - i * 20,
        toX: w + baseW + 40,
        fromY: h * spec.y0,
        toY: h * spec.y1,
        amp: h * spec.amp,
        rot: spec.rot,
        rot2: spec.rot2,
        opacityIn: 0.20,
        opacityOut: 0.84
      };
    });
  }

  function createLayer(hero, images) {
    if (getComputedStyle(hero).position === 'static') hero.style.position = 'relative';
    var stage = document.createElement('div');
    stage.className = ROOT;
    stage.setAttribute('aria-hidden', 'true');
    stage.style.cssText = [
      'position:absolute', 'inset:0', 'overflow:hidden', 'pointer-events:none',
      'user-select:none', 'z-index:' + Z_INDEX
    ].join(';') + ';';
    var sprites = images.map(function (image, idx) {
      var img = document.createElement('img');
      img.src = image.src;
      img.alt = '';
      img.decoding = 'async';
      img.draggable = false;
      img.style.cssText = [
        'position:absolute', 'left:0', 'top:0', 'display:block',
        'height:auto', 'max-width:none', 'pointer-events:none',
        'opacity:0', 'will-change:transform,opacity'
      ].join(';') + ';';
      stage.appendChild(img);
      return img;
    });
    hero.appendChild(stage);
    return { stage: stage, sprites: sprites };
  }

  function install(hero) {
    if (mounted.has(hero)) return;
    mounted.add(hero);

    var started = false;
    var roseObserver = null;
    var waitObserver = null;
    var delayTimer = null;

    function stopObserving() {
      if (roseObserver) { roseObserver.disconnect(); roseObserver = null; }
      if (waitObserver) { waitObserver.disconnect(); waitObserver = null; }
    }

    function showReducedMotion(images) {
      if (!images || !hero.isConnected || hero.querySelector('.' + ROOT)) return;
      var layer = createLayer(hero, [images[0]]);
      var img = layer.sprites[0];
      img.style.width = 'clamp(42px,5vw,72px)';
      img.style.opacity = '0.88';
      img.style.transform = 'translate3d(2.5vw,' + Math.round((hero.clientHeight || 560) * 0.48) + 'px,0) rotate(-8deg)';
    }

    function run() {
      if (started) return;
      started = true;
      stopObserving();
      delayTimer = window.setTimeout(function () {
        imageReady.then(function (images) {
          if (!images || !hero.isConnected || hero.querySelector('.' + ROOT)) return;
          if (reducedMotion) { showReducedMotion(images); return; }

          var layer = createLayer(hero, images);
          var stage = layer.stage;
          var sprites = layer.sprites;
          var specs = buildSpecs(hero);
          var startAt = null;
          var pausedAt = null;
          var raf = 0;

          specs.forEach(function (spec, i) {
            sprites[i].style.width = spec.width.toFixed(1) + 'px';
          });

          function cleanup() {
            window.cancelAnimationFrame(raf);
            document.removeEventListener('visibilitychange', visibilityChanged);
            if (stage && stage.parentNode) stage.parentNode.removeChild(stage);
          }

          function updateSprite(node, spec, elapsed) {
            if (elapsed < spec.startMs || elapsed > spec.endMs) {
              node.style.opacity = '0';
              return;
            }
            var local = (elapsed - spec.startMs) / Math.max(1, spec.endMs - spec.startMs);
            var e = ease(local);
            var x = spec.fromX + (spec.toX - spec.fromX) * e;
            var y = spec.fromY + (spec.toY - spec.fromY) * e + spec.amp * Math.sin(local * Math.PI * 2.5);
            var rot = spec.rot + (spec.rot2 - spec.rot) * e + 5 * Math.sin(local * Math.PI * 3.2);
            var opacity = local < 0.12 ? (local / 0.12) * spec.opacityOut :
              (local > 0.85 ? (1 - (local - 0.85) / 0.15) * spec.opacityOut : spec.opacityOut);
            node.style.opacity = String(Math.max(0, Math.min(1, opacity)));
            node.style.transform = 'translate3d(' + x.toFixed(1) + 'px,' + y.toFixed(1) + 'px,0) rotate(' + rot.toFixed(2) + 'deg)';
          }

          function tick(now) {
            if (!hero.isConnected || !stage.isConnected) { cleanup(); return; }
            if (document.hidden) return;
            if (startAt === null) startAt = now;
            var elapsed = now - startAt;
            sprites.forEach(function (node, i) { updateSprite(node, specs[i], elapsed); });
            if (elapsed >= RUN_MS) { cleanup(); return; }
            raf = window.requestAnimationFrame(tick);
          }

          function visibilityChanged() {
            if (document.hidden) {
              pausedAt = performance.now();
              window.cancelAnimationFrame(raf);
            } else {
              if (pausedAt !== null && startAt !== null) startAt += performance.now() - pausedAt;
              pausedAt = null;
              raf = window.requestAnimationFrame(tick);
            }
          }

          document.addEventListener('visibilitychange', visibilityChanged);
          if (!document.hidden) raf = window.requestAnimationFrame(tick);
        });
      }, START_DELAY_MS);
    }

    function observeRose(rose) {
      roseObserver = new MutationObserver(function () {
        if (roseAlmostFinished(rose)) run();
      });
      roseObserver.observe(rose, { attributes: true, attributeFilter: ['style'] });
      if (roseAlmostFinished(rose)) run();
    }

    var rose = hero.querySelector('.' + ROSE);
    if (rose) observeRose(rose);
    else {
      waitObserver = new MutationObserver(function () {
        var found = hero.querySelector('.' + ROSE);
        if (found) {
          if (waitObserver) { waitObserver.disconnect(); waitObserver = null; }
          observeRose(found);
        }
      });
      waitObserver.observe(hero, { childList: true, subtree: true });
    }
  }

  function mount() {
    var hero = findHero();
    if (hero) install(hero);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount, { once: true });
  } else {
    mount();
  }
  document.addEventListener('shopify:section:load', function () {
    window.requestAnimationFrame(mount);
  });
})();
